import React from 'react';
import { Search, MessageSquare, Phone } from 'lucide-react';

const IsometricAIPhone = () => {
    // Standard Isometric rotation
    const sceneRotation = {
        transform: 'rotateX(55deg) rotateZ(45deg)',
        transformStyle: 'preserve-3d' as const
    };

    return (
        <div className="w-full h-full flex items-center justify-center scale-75 md:scale-100">
            <div
                className="relative w-[280px] h-[280px]"
                style={sceneRotation}
            >
                {/* Main Base Layer */}
                <div
                    className="absolute top-0 left-0 w-full h-full"
                    style={{
                        transform: 'translateZ(0px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Shadow */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-900/40 blur-2xl"
                        style={{
                            transform: 'translateZ(-60px) translateY(40px) translateX(40px)',
                        }}
                    />

                    {/* Thickness Slices (Blue Base) */}
                    {Array.from({ length: 35 }).map((_, i) => (
                        <div
                            key={i}
                            className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-700"
                            style={{
                                transform: `translateZ(-${i + 1}px)`,
                            }}
                        />
                    ))}

                    {/* Top Face */}
                    <div
                        className="absolute top-0 left-0 w-full h-full rounded-3xl bg-indigo-600"
                        style={{
                            transform: 'translateZ(0px)',
                            boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.1)'
                        }}
                    >
                        {/* Subtle surface detail */}
                        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />
                    </div>
                </div>

                {/* Surface Elements (Flat on the board) */}

                {/* Black Card (Top Left) */}
                <div
                    className="absolute"
                    style={{
                        left: '30px',
                        top: '30px',
                        width: '110px',
                        height: '110px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-gray-900" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-gray-900 rounded-2xl flex items-center justify-center shadow-sm border border-gray-700">
                        <div className="w-12 h-8 bg-gray-700/50 rounded"></div>
                    </div>
                </div>

                {/* Orange Card (Bottom Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '30px',
                        bottom: '30px',
                        width: '110px',
                        height: '80px',
                        transform: 'translateZ(2px)',
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Card Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-orange-700" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-orange-500 rounded-2xl flex items-center justify-center shadow-sm border border-orange-400">
                        <div className="w-16 h-3 bg-orange-200/50 rounded-full"></div>
                    </div>
                </div>


                {/* Floating Elements - Parallel to Base (Isometric) */}

                {/* Search Bubble (Top/Back) */}
                <div
                    className="absolute"
                    style={{
                        left: '100px',
                        top: '-20px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(80px) rotateZ(-3deg) rotateX(-90deg)', // Standing up, Right side forward
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-gray-100" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-indigo-50">
                        <Search className="w-10 h-10 text-indigo-500" />
                    </div>
                </div>

                {/* Chat Bubble (Right) */}
                <div
                    className="absolute"
                    style={{
                        right: '-40px',
                        top: '70px',
                        width: '80px',
                        height: '80px',
                        transform: 'translateZ(60px)', // Just lift up, keep parallel
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-2xl bg-gray-100" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}
                    <div className="absolute inset-0 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-indigo-50">
                        <MessageSquare className="w-10 h-10 text-indigo-500" />
                    </div>
                </div>

                {/* Shadow for Green Phone (Independent) */}
                <div
                    className="absolute"
                    style={{
                        left: '10%',
                        top: '60%',
                        width: '80px',
                        height: '80px',
                        marginLeft: '-40px',
                        marginTop: '-40px',
                        transform: 'translateZ(0px)', // On the surface
                    }}
                >
                    <div className="w-full h-full bg-black/20 blur-xl rounded-full" />
                </div>

                {/* Green Phone Circle (Center) */}
                <div
                    className="absolute"
                    style={{
                        left: '0%',
                        top: '70%',
                        width: '100px',
                        height: '100px',
                        marginLeft: '-50px',
                        marginTop: '-50px',
                        transform: 'translateZ(80px) rotateZ(-80deg) rotateX(-77deg)', // Upright, angled left
                        transformStyle: 'preserve-3d'
                    }}
                >
                    {/* Thickness */}
                    {Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="absolute inset-0 rounded-full bg-green-600" style={{ transform: `translateZ(-${i}px)` }} />
                    ))}

                    <div className="absolute inset-0 bg-green-500 rounded-full shadow-2xl flex items-center justify-center border-[6px] border-white">
                        <Phone className="w-12 h-12 text-white" />
                    </div>
                </div>

            </div>
        </div>
    );
};

export default IsometricAIPhone;
